import React, { useEffect, useState } from 'react';
import {
    Alert, AlertIcon, Badge, Box, Button, Checkbox, Flex, FormControl, FormHelperText, FormLabel, HStack,
    Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    SimpleGrid, Spinner, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, useDisclosure, useToast, Wrap, WrapItem,
} from '@chakra-ui/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_AWS_API_URL || '';

const EMPTY = {
    name: '', street: '', city: '', state: '', zipCode: '', phone: '', website: '',
    services: 'Wash & fold', description: '', offersDelivery: false, serviceableZipCodes: '', hidden: false,
};

/**
 * Platform-admin only: laundromats that are NOT on Smart Laundry Basket, shown as
 * basic listings in the "Wash & Fold Near Me" directory below our own shops.
 * Enter only public information or details the owner gave you.
 */
export default function DirectoryOutsideSection({ platformKey, onAuthError }) {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const headers = { 'x-platform-key': platformKey };

    const fail = (err, title) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) { onAuthError && onAuthError(); return; }
        toast({ title, description: err?.response?.data?.detail || err.message, status: 'error', duration: 5000 });
    };

    const load = () => {
        axios.get(`${API_URL}/api/directory/admin/outside`, { headers })
            .then(({ data: d }) => setData(d))
            .catch((err) => fail(err, 'Could not load listings'));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, []);

    const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const openNew = () => { setForm(EMPTY); setEditingId(null); onOpen(); };
    const openEdit = (l) => {
        setForm({ ...EMPTY, ...l, serviceableZipCodes: (l.serviceableZipCodes || []).join(', ') });
        setEditingId(l.id);
        onOpen();
    };

    const save = async (extra = {}) => {
        setSaving(true);
        const body = {
            ...form,
            ...extra,
            serviceableZipCodes: String(form.serviceableZipCodes || '').split(/[\s,]+/).filter(Boolean),
        };
        try {
            if (editingId) await axios.put(`${API_URL}/api/directory/admin/outside/${editingId}`, body, { headers });
            else await axios.post(`${API_URL}/api/directory/admin/outside`, body, { headers });
            toast({ title: editingId ? 'Listing updated' : 'Listing added', status: 'success', duration: 2500 });
            onClose();
            load();
        } catch (err) {
            fail(err, 'Could not save listing');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (l) => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Delete the listing for ${l.name}? This can't be undone.`)) return;
        try {
            await axios.delete(`${API_URL}/api/directory/admin/outside/${l.id}`, { headers });
            load();
        } catch (err) {
            fail(err, 'Could not delete listing');
        }
    };

    if (!data) return <Flex justify="center" py={10}><Spinner /></Flex>;

    const status = (l) => {
        if (l.claimedLaundryId) return <Badge colorScheme="green">Claimed by #{l.claimedLaundryId}</Badge>;
        if (l.pendingReview) {
            return <Badge colorScheme="orange">{l.replacesListingId ? `Claim of #${l.replacesListingId}` : 'Owner request'}: review</Badge>;
        }
        if (l.removalRequestedAt) return <Badge colorScheme="red">Removal requested</Badge>;
        if (l.hidden) return <Badge>Hidden</Badge>;
        return <Badge colorScheme="blue">Live</Badge>;
    };

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={3} gap={4} wrap="wrap">
                <Box>
                    <Text fontWeight="bold" fontSize="lg">Directory: outside laundromats</Text>
                    <Text fontSize="sm" color="gray.600">
                        Basic listings for laundromats not on Smart Laundry Basket. They show below our shops with Call and
                        Website buttons and a "Claim it free" link. Use public info or details the owner gave you, never data
                        copied from Google Maps, and write a short description in your own words for each.
                    </Text>
                </Box>
                <Button colorScheme="blue" onClick={openNew}>Add laundromat</Button>
            </Flex>

            {data.waitlistByZip?.length > 0 && (
                <Box mb={4} p={3} bg="purple.50" borderRadius="md">
                    <Text fontSize="sm" fontWeight="600" mb={2}>Customers waiting for a laundromat (by ZIP)</Text>
                    <Wrap>
                        {data.waitlistByZip.map((w) => (
                            <WrapItem key={w.zip}><Badge colorScheme="purple">{w.zip}: {w.count}</Badge></WrapItem>
                        ))}
                    </Wrap>
                </Box>
            )}

            {data.listings.length === 0 ? (
                <Alert status="info" borderRadius="md"><AlertIcon />No outside laundromats yet. Add your first one.</Alert>
            ) : (
                <Box overflowX="auto">
                    <Table size="sm">
                        <Thead>
                            <Tr><Th>Name</Th><Th>City</Th><Th>Phone</Th><Th>Status</Th><Th isNumeric>Views (30d)</Th><Th isNumeric>Clicks (30d)</Th><Th /></Tr>
                        </Thead>
                        <Tbody>
                            {data.listings.map((l) => (
                                <Tr key={l.id}>
                                    <Td fontWeight="600">{l.name}</Td>
                                    <Td>{l.city}, {l.state}</Td>
                                    <Td>{l.phone}</Td>
                                    <Td>
                                        {status(l)}
                                        {l.removalNote && <Text fontSize="xs" color="gray.500">"{l.removalNote}"</Text>}
                                        {l.contactEmail && <Text fontSize="xs" color="gray.500">{l.contactName} {l.contactEmail}</Text>}
                                    </Td>
                                    <Td isNumeric>{l.views30d}</Td>
                                    <Td isNumeric>{l.clicks30d}</Td>
                                    <Td>
                                        <HStack justify="flex-end">
                                            <Button size="xs" onClick={() => openEdit(l)}>{l.pendingReview ? 'Review' : 'Edit'}</Button>
                                            <Button size="xs" colorScheme="red" variant="ghost" onClick={() => remove(l)}>Delete</Button>
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            )}

            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editingId ? 'Edit laundromat' : 'Add laundromat'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {form.pendingReview && (
                            <Alert status="warning" borderRadius="md" mb={4} fontSize="sm">
                                <AlertIcon />
                                Submitted by {form.contactName || 'the owner'} ({form.contactEmail}).
                                {form.replacesListingId ? ` Approving replaces listing #${form.replacesListingId}.` : ''}
                                {' '}Check it's a real laundromat, then approve. Let them know by email once it's live.
                            </Alert>
                        )}
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            <FormControl isRequired gridColumn={{ md: 'span 2' }}>
                                <FormLabel>Name</FormLabel>
                                <Input value={form.name} onChange={(e) => up('name', e.target.value)} />
                            </FormControl>
                            <FormControl gridColumn={{ md: 'span 2' }}>
                                <FormLabel>Street</FormLabel>
                                <Input value={form.street} onChange={(e) => up('street', e.target.value)} />
                                <FormHelperText>Leave blank if they don't take walk-in drop-offs.</FormHelperText>
                            </FormControl>
                            <FormControl isRequired><FormLabel>City</FormLabel>
                                <Input value={form.city} onChange={(e) => up('city', e.target.value)} /></FormControl>
                            <SimpleGrid columns={2} spacing={3}>
                                <FormControl isRequired><FormLabel>State</FormLabel>
                                    <Input value={form.state} maxLength={2} onChange={(e) => up('state', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>ZIP</FormLabel>
                                    <Input value={form.zipCode} maxLength={5} onChange={(e) => up('zipCode', e.target.value)} /></FormControl>
                            </SimpleGrid>
                            <FormControl><FormLabel>Phone</FormLabel>
                                <Input value={form.phone} onChange={(e) => up('phone', e.target.value)} /></FormControl>
                            <FormControl><FormLabel>Website</FormLabel>
                                <Input value={form.website} placeholder="https://" onChange={(e) => up('website', e.target.value)} /></FormControl>
                            <FormControl gridColumn={{ md: 'span 2' }}>
                                <FormLabel>Services</FormLabel>
                                <Input value={form.services} onChange={(e) => up('services', e.target.value)} />
                                <FormHelperText>Comma separated, e.g. Wash &amp; fold, Self-service, Dry cleaning.</FormHelperText>
                            </FormControl>
                            <FormControl gridColumn={{ md: 'span 2' }}>
                                <FormLabel>Description</FormLabel>
                                <Textarea rows={3} value={form.description} onChange={(e) => up('description', e.target.value)}
                                    placeholder="One or two sentences in your own words." />
                            </FormControl>
                            <FormControl gridColumn={{ md: 'span 2' }}>
                                <Checkbox isChecked={form.offersDelivery} onChange={(e) => up('offersDelivery', e.target.checked)}>
                                    Offers pickup &amp; delivery
                                </Checkbox>
                            </FormControl>
                            {form.offersDelivery && (
                                <FormControl gridColumn={{ md: 'span 2' }}>
                                    <FormLabel>Delivery ZIP codes</FormLabel>
                                    <Input value={form.serviceableZipCodes} onChange={(e) => up('serviceableZipCodes', e.target.value)}
                                        placeholder="78664, 78665" />
                                </FormControl>
                            )}
                            {editingId && (
                                <FormControl gridColumn={{ md: 'span 2' }}>
                                    <Checkbox isChecked={form.hidden} onChange={(e) => up('hidden', e.target.checked)}>
                                        Hidden from the directory
                                    </Checkbox>
                                </FormControl>
                            )}
                        </SimpleGrid>
                    </ModalBody>
                    <ModalFooter>
                        {editingId && form.claimedLaundryId && (
                            <Button mr="auto" size="sm" variant="ghost" onClick={() => save({ unclaim: true })} isLoading={saving}>
                                Undo claim
                            </Button>
                        )}
                        <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
                        {form.pendingReview ? (
                            <Button colorScheme="green" onClick={() => save({ hidden: false })} isLoading={saving}>Approve &amp; publish</Button>
                        ) : (
                            <Button colorScheme="blue" onClick={() => save()} isLoading={saving}>Save</Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
